// Library Import
import {
  Box,
  Button,
  FormControl,
  Input,
  Text,
  VStack,
  Icon,
  Link,
  InputGroup,
  InputLeftElement,
  Image,
  Flex,
  useColorModeValue,
  useDisclosure,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  AspectRatio,
  useColorMode,
} from "@chakra-ui/react";
import { motion } from "framer-motion";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { passwordStrength } from "check-password-strength";

// Icon Imports
import { FiArrowLeft, FiMail } from "react-icons/fi";
import { FaPlay } from "react-icons/fa";
import { MoonIcon, SunIcon } from "@chakra-ui/icons";

// Local Imports
import {
  createResetPassword,
  resetPassword,
} from "../../redux/thunks/userThunks";
import { useEffect } from "react";
import {
  redResetPasswordEmail,
  redResetPasswordUpdated,
  redSetToast,
} from "../../redux/slices/userSlice";
import Toasts from "../../components/Toasts";
import Navbar from "../../components/Navbar";

export default function ForgotPasswordScreen() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { colorMode, toggleColorMode } = useColorMode();

  const [searchParam] = useSearchParams();

  const { loading, resetPasswordEmail, passwordUpdated } = useSelector(
    (state) => state.stateUserReducer
  );

  const [email, setEmail] = useState("");
  const [token] = useState(searchParam.get("token"));
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");

  const handleResetPassword = () => {
    if (token) {
      if (
        !(
          passwordStrength(newPassword).value === "Medium" ||
          passwordStrength(newPassword).value === "Strong"
        )
      ) {
        dispatch(
          redSetToast({
            title: "Set a strong password",
            status: "error",
          })
        );
        return;
      }

      if (newPassword !== confirmPassword) {
        dispatch(
          redSetToast({
            title: "Passwords do not match",
            status: "error",
          })
        );
        return;
      }

      dispatch(resetPassword({ password: newPassword, token }));
    } else {
      dispatch(createResetPassword(email));
    }
  };

  useEffect(() => {
    if (resetPasswordEmail) {
      dispatch(
        redSetToast({ title: "Email sent successfully", status: "success" })
      );

      dispatch(redResetPasswordEmail());
    }

    if (passwordUpdated) {
      dispatch(
        redSetToast({
          title: "Password updated successfully",
          status: "success",
        })
      );

      dispatch(redResetPasswordUpdated());
      setNewPassword("");
      setConfirmPassword("");
    }
  }, [resetPasswordEmail, passwordUpdated]);

  return (
    <>
      <Toasts />
      <Navbar active="ChallengeScreen" onOpen={onOpen} />
      {/* Video Modal */}
      <Modal isOpen={isOpen} onClose={onClose} size="5xl" isCentered>
        <ModalOverlay bg="blackAlpha.600" backdropFilter="blur(10px)" />
        <ModalContent minH="80vh" my="auto">
          <ModalHeader>Portal Guide</ModalHeader>
          <ModalCloseButton />
          <ModalBody pb={6}>
            <AspectRatio ratio={16 / 9} h="full">
              <iframe
                src="https://www.youtube.com/embed/MbcjyywlfOo?rel=0&modestbranding=1"
                title="Portal Guide"
                allowFullScreen
              />
            </AspectRatio>
          </ModalBody>
        </ModalContent>
      </Modal>

      <Flex
        position="relative"
        minH="90vh"
        w="100%"
        direction="column"
        align="center"
        bg={useColorModeValue("white", "gray.800")}
        overflow="hidden"
        justifyContent="center"
        alignItems="center"
      >
        <Flex
          position="absolute"
          inset={0}
          bg={useColorModeValue(
            "radial-gradient(#9B9B9B, 1.3px, transparent 1px)",
            "radial-gradient(#666666, 1.3px, transparent 1px)"
          )}
          backgroundSize="25px 25px"
          opacity={0.4}
          sx={{
            maskImage:
              "radial-gradient(ellipse 50% 50% at 50% 50%, #000 70%, transparent 100%)",
            WebkitMaskImage:
              "radial-gradient(ellipse 50% 50% at 50% 50%, #000 70%, transparent 100%)",
          }}
        />

        <Box
          maxW="400px"
          w="90%"
          bg={useColorModeValue("white", "gray.700")}
          border={"1.5px solid black"}
          borderRadius="2xl"
          p={8}
          boxShadow="xl"
          zIndex={10}
        >
          <VStack spacing={6}>
            <Text
              fontSize="3xl"
              fontWeight="bold"
              color={useColorModeValue("gray.800", "blue.300")}
            >
              {token ? "Reset Password" : "Forgot Password"}
            </Text>
            {!token ? (
              <VStack gap={5}>
                <Text
                  color={useColorModeValue("gray.600", "gray.300")}
                  textAlign="center"
                >
                  No worries! Enter your email and we'll send you reset
                  instructions.
                </Text>
                <FormControl>
                  <InputGroup>
                    <InputLeftElement pointerEvents="none">
                      <Icon as={FiMail} color="gray.300" mt="10px" />
                    </InputLeftElement>
                    <Input
                      type="email"
                      placeholder="Enter your email"
                      size="lg"
                      borderRadius="full"
                      bg={useColorModeValue("white", "gray.700")}
                      border={"1px solid"}
                      borderColor={"gray.500"}
                      color={useColorModeValue("gray.800", "white")}
                      _focus={{
                        borderColor: "blue.500",
                        boxShadow: "0 0 0 1px #3182ce",
                      }}
                      _placeholder={{
                        color: useColorModeValue("gray.500", "gray.400"),
                      }}
                      onChange={(e) => setEmail(e.target.value)}
                    />
                  </InputGroup>
                </FormControl>
              </VStack>
            ) : (
              <VStack gap={5} w="full" align="flex-start">
                <FormControl>
                  <InputGroup>
                    <InputLeftElement pointerEvents="none">
                      <Icon as={FiMail} color="gray.300" mt="10px" />
                    </InputLeftElement>
                    <Input
                      type="password"
                      value={newPassword}
                      placeholder="Enter new password"
                      size="lg"
                      borderRadius="full"
                      bg={useColorModeValue("white", "gray.700")}
                      border={"1px solid"}
                      borderColor={"gray.500"}
                      color={useColorModeValue("gray.800", "white")}
                      _focus={{
                        borderColor: "blue.500",
                        boxShadow: "0 0 0 1px #3182ce",
                      }}
                      _placeholder={{
                        color: useColorModeValue("gray.500", "gray.400"),
                      }}
                      onChange={(e) => setNewPassword(e.target.value)}
                    />
                  </InputGroup>
                </FormControl>

                <FormControl>
                  <InputGroup>
                    <InputLeftElement pointerEvents="none">
                      <Icon as={FiMail} color="gray.300" mt="10px" />
                    </InputLeftElement>
                    <Input
                      type="password"
                      value={confirmPassword}
                      placeholder="Confirm new passoword"
                      size="lg"
                      borderRadius="full"
                      bg={useColorModeValue("white", "gray.700")}
                      color={useColorModeValue("gray.800", "white")}
                      border={"1px solid"}
                      borderColor={"gray.500"}
                      _focus={{
                        borderColor: "blue.500",
                        boxShadow: "0 0 0 1px #3182ce",
                      }}
                      _placeholder={{
                        color: useColorModeValue("gray.500", "gray.400"),
                      }}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                    />
                  </InputGroup>
                </FormControl>

                {newPassword && (
                  <Text fontSize="15px" fontWeight="bold" ml={2}>
                    Your password is{" "}
                    <Text
                      as="span"
                      color={
                        passwordStrength(newPassword).value === "Too weak"
                          ? "red.500"
                          : passwordStrength(newPassword).value === "Weak"
                          ? "orange.400"
                          : passwordStrength(newPassword).value === "Medium"
                          ? "yellow.500"
                          : "green.500"
                      }
                      fontWeight="extrabold"
                    >
                      {passwordStrength(newPassword).value}
                    </Text>
                  </Text>
                )}
              </VStack>
            )}

            <Button
              w="full"
              size="lg"
              bgColor={useColorModeValue("black", "gray.600")}
              color={useColorModeValue("white", "gray.600")}
              _hover={{
                bgColor: "black",
              }}
              borderRadius="full"
              fontWeight="bold"
              as={motion.button}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={handleResetPassword}
              isLoading={loading}
            >
              Reset Password
            </Button>
            <Link
              color="black"
              fontWeight="medium"
              fontSize="sm"
              display="flex"
              alignItems="center"
              as={motion.a}
              whileHover={{ x: -2 }}
              onClick={() => navigate("/")}
            >
              <Icon as={FiArrowLeft} mr={2} />
              Go Back
            </Link>
          </VStack>
        </Box>
      </Flex>
    </>
  );
}
